import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import EasyMDE from 'easymde';
import { LoggerService } from '../../../../core/services/logger.service';
import { MetadataHelpDialogComponent } from '../../../../shared/components/metadata-help-dialog/metadata-help-dialog.component';
import { MarkdownPipe } from '../../../../shared/pipes/markdown.pipe';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-text-label-mde-editor',
  templateUrl: './text-label-mde-editor.component.html',
  styleUrl: './text-label-mde-editor.component.css',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, TranslatePipe, MarkdownPipe, MetadataHelpDialogComponent],
})
export class TextLabelMdeEditorComponent implements AfterViewInit, OnDestroy {
  @ViewChild('editorElement') editorElement?: ElementRef<HTMLTextAreaElement>;

  private log = inject(LoggerService).getLogger('mde-editor');

  value = input<string>('');
  placeholder = input<string>('Enter markdown content...');
  valueChange = output<string>();

  editor: any = null;
  isMobile = signal(false);
  editorInitialized = signal(false);
  editorError = signal(false);
  showVerticalPreview = signal(false);
  previewMarkdown = signal('');
  showMetadataHelp = signal(false);

  constructor() {
    // Monitor mobile state using body classes
    effect(() => {
      const body = document.body;
      const isMobileSize =
        body.classList.contains('screen-xs') || body.classList.contains('screen-sm');
      this.isMobile.set(isMobileSize);
    });
  }

  ngAfterViewInit(): void {
    this.initializeEditor();
  }

  ngOnDestroy(): void {
    if (this.editor) {
      try {
        this.editor.toTextArea();
        this.editor = null;
      } catch (error) {
        this.log.error('[MDE] Error cleaning up editor:', error);
      }
    }
  }

  private initializeEditor(): void {
    try {
      if (!this.editorElement) {
        this.log.warn('[MDE] Editor element not found');
        this.editorInitialized.set(false);
        this.editorError.set(true);
        return;
      }

      // Get textarea element
      const textArea = this.editorElement.nativeElement;

      // Explicitly set the textarea value before EasyMDE initializes
      // This ensures no stray whitespace is interpreted as content
      textArea.value = this.value() || '';

      const editorConfig: EasyMDE.Options = {
        element: textArea,
        spellChecker: false,
        tabSize: 2,
        autoDownloadFontAwesome: true,
        sideBySideFullscreen: false,
        initialValue: this.value() || '',
        placeholder: this.placeholder() || 'Enter markdown content...',
        toolbar: [
          'bold',
          'italic',
          'heading',
          '|',
          'quote',
          'unordered-list',
          'ordered-list',
          '|',
          'link',
          'image',
          '|',
          'preview',
          'side-by-side',
          {
            name: 'preview-below',
            action: () => this.toggleVerticalPreview(),
            className: 'fa fa-columns fa-rotate-90',
            title: 'Preview Below',
          },
          'fullscreen',
          '|',
          'guide',
          {
            name: 'metadata-help',
            action: () => this.openMetadataHelp(),
            className: 'fa fa-info-circle',
            title: 'Insert checkpoint metadata',
          },
        ],
        status: ['lines', 'words', 'cursor'] as const,
      };

      this.editor = new EasyMDE(editorConfig);
      this.editorInitialized.set(true);

      // Sync editor content with output
      this.editor.codemirror.on('change', () => {
        try {
          const editorValue = this.editor.value();
          this.valueChange.emit(editorValue);
          // Update vertical preview if active
          if (this.showVerticalPreview()) {
            this.updateVerticalPreview();
          }
        } catch (error) {
          this.log.error('[MDE] Error syncing editor content:', error);
        }
      });

      // Handle editor errors
      this.editor.codemirror.on('error', () => {
        this.log.error('[MDE] CodeMirror error detected');
        this.editorError.set(true);
      });

      this.log.debug('[MDE] Editor initialized successfully');
    } catch (error) {
      this.log.error('[MDE] Failed to initialize editor:', error);
      this.editorInitialized.set(false);
      this.editorError.set(true);
    }
  }

  /**
   * Handle change event from fallback textarea
   */
  onFallbackChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    if (target && target.value !== undefined) {
      this.valueChange.emit(target.value);
    }
  }

  /**
   * Update editor value
   */
  updateValue(newValue: string): void {
    if (this.editor) {
      try {
        this.editor.value(newValue);
      } catch (error) {
        this.log.error('[MDE] Error updating editor value:', error);
      }
    }
  }

  /**
   * Get current editor value
   */
  getValue(): string {
    if (this.editor) {
      try {
        return this.editor.value();
      } catch (error) {
        this.log.error('[MDE] Error getting editor value:', error);
        return this.value();
      }
    }
    return this.value();
  }

  /**
   * Open the checkpoint metadata placeholder help dialog
   */
  openMetadataHelp(): void {
    this.showMetadataHelp.set(true);
  }

  /**
   * Toggle vertical preview below editor
   */
  toggleVerticalPreview(): void {
    this.showVerticalPreview.set(!this.showVerticalPreview());
    if (this.showVerticalPreview()) {
      this.updateVerticalPreview();
    }
  }

  /**
   * Update vertical preview content
   */
  private updateVerticalPreview(): void {
    if (!this.editor) return;
    this.previewMarkdown.set(this.editor.value() || '');
  }
}
